import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

interface Option {
    value: string;
    label: string;
}

interface MultiSelectProps {
    options: Option[];
    value: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
    allLabel?: string;
    enableSearch?: boolean;
    searchPlaceholder?: string;
}

export default function MultiSelect({
    options,
    value: selected,
    onChange,
    placeholder = "Select...",
    allLabel = "All",
    enableSearch = false,
    searchPlaceholder = "Search options..."
}: MultiSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [query, setQuery] = useState("");

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleOption = (value: string) => {
        if (value === 'all') {
            onChange([]);
            return;
        }

        if (selected.includes(value)) {
            onChange(selected.filter(v => v !== value));
        } else {
            onChange([...selected, value]);
        }
    };

    const clearAll = () => {
        onChange([]);
        setIsOpen(false);
        setQuery("");
    };

    const isAllSelected = selected.length === 0;

    const getDisplayText = () => {
        if (isAllSelected) {
            return allLabel;
        }
        if (selected.length === 1) {
            const option = options.find(opt => opt.value === selected[0]);
            return option?.label || selected[0];
        }
        return `${selected.length} selected`;
    };

    const filteredOptions = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!enableSearch || !q) return options;
        return options.filter(opt => opt.label.toLowerCase().includes(q));
    }, [options, query, enableSearch]);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white text-left rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors flex items-center justify-between ${!isAllSelected ? 'border-blue-500 bg-blue-900/20' : ''
                    }`}
            >
                <span className={`truncate ${isAllSelected ? 'text-gray-300' : 'text-white'}`}>
                    {getDisplayText()}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Clear button when filters are active */}
            {!isAllSelected && (
                <button
                    onClick={clearAll}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white rounded-full text-xs hover:bg-red-700 transition-colors flex items-center justify-center z-10"
                    title="Clear filters"
                >
                    <X className="w-3 h-3" />
                </button>
            )}

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {enableSearch && (
                        <div className="p-2 border-b border-gray-700 sticky top-0 bg-gray-800">
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={searchPlaceholder || 'Search'}
                                className="w-full px-2 py-1 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    )}
                    {/* All option */}
                    <button
                        type="button"
                        onClick={() => toggleOption('all')}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-700 transition-colors flex items-center justify-between ${isAllSelected ? 'bg-blue-900/30 text-blue-300' : 'text-gray-300'
                            }`}
                    >
                        <span>{allLabel}</span>
                        {isAllSelected && <Check className="w-4 h-4 text-blue-400" />}
                    </button>

                    <div className="border-t border-gray-700"></div>

                    {/* Individual options */}
                    {filteredOptions.map((option) => {
                        const isSelected = selected.includes(option.value);
                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => toggleOption(option.value)}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-700 transition-colors flex items-center justify-between ${isSelected ? 'bg-blue-900/30 text-blue-300' : 'text-gray-300'
                                    }`}
                            >
                                <span>{option.label}</span>
                                {isSelected && <Check className="w-4 h-4 text-blue-400" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
